// Continuous motion on the puppet stage (docs/choreo-proposal.md).
//
// groove  a transform loop that runs underneath everything a character does (a bop, a sway, a step-touch), at a
//         tempo, until it is changed: the body moving to the music. It never changes the picture.
// choreo  a sequence of pose changes with timing (the disco point up and down, a clap on beat 3 of every other bar,
//         a jump then a landing). It plays once or loops, and starts when it is triggered or on the next beat or
//         bar of the beat clock, so a dance line moves in unison and a seek lands on the right step.
//
// Both are optional fields of the `pose` action. Everything here is plain data plus the timing arithmetic the player
// and the server share: which step is showing at a given moment, and how long a one-off takes.
import { z } from "zod";
import { zChoreoAccent } from "./choreoAccent";
export * from "./choreoAccent";

const poseToken = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9_-]+$/);

/** The transform loops. Lengths are in beats; one cycle of each is one loop of the animation. */
export const GROOVES = ["bop", "sway", "step", "shimmy", "hop", "groove"] as const;
export const zGroove = z.enum(GROOVES);
export type Groove = z.infer<typeof zGroove>;
export const GROOVE_BEATS: Record<Groove, number> = { bop: 1, sway: 2, step: 2, shimmy: 1, hop: 2, groove: 2 };

/** Tempo bounds for a groove or a choreo, and the tempo used when nothing says otherwise. */
export const MIN_BPM = 40;
export const MAX_BPM = 240;
export const DEFAULT_BPM = 100;
export const zBpm = z.number().int().min(MIN_BPM).max(MAX_BPM);

export const CHOREO_SYNC = ["trigger", "beat", "bar"] as const;
export type ChoreoSync = (typeof CHOREO_SYNC)[number];

/** One step: a pose (or a pool to pick from) held for a number of beats or milliseconds. */
export const zChoreoStep = z
  .object({
    /** A panel token ("C9") or a pool picked from at random each time; absent keeps the current pose. */
    pose: z.union([poseToken, z.array(poseToken).min(1).max(16)]).optional(),
    beats: z.number().min(0.25).max(32).optional(),
    durationMs: z.number().int().min(50).max(60000).optional(),
    /** A random share (0-1) a millisecond step may be longer or shorter by, so idles do not look mechanical. */
    vary: z.number().min(0).max(1).optional(),
    /** A one-shot gesture as the step begins. */
    gesture: z.enum(["giggle", "bounce", "nod", "headshake", "wiggle"]).optional(),
    facing: z.enum(["left", "right"]).optional(),
    accent: zChoreoAccent.optional(),
    description: z.string().max(200).optional(),
  })
  .strict();
export type ChoreoStep = z.infer<typeof zChoreoStep>;

export const zChoreo = z
  .object({
    /** A well-known or reusable id ("disco-point", "clap-on-three"); inline choreos may leave it out. */
    id: poseToken.optional(),
    name: z.string().max(80).optional(),
    poses: z.array(zChoreoStep).min(1).max(32),
    /** Loop until changed (otherwise it plays once). The trigger's `play` overrides this. */
    loop: z.boolean().optional(),
    /** When it starts and which clock it follows. Default: "bar" for beat steps, "trigger" for millisecond steps. */
    sync: z.enum(CHOREO_SYNC).optional(),
    /** The pose a one-off ends on (otherwise the pose held before it). */
    settle: poseToken.optional(),
    /** A groove running underneath while it plays. */
    groove: zGroove.optional(),
    /** The tempo for beat steps when no song sets one. */
    bpm: zBpm.optional(),
  })
  .strict()
  .superRefine((choreo, ctx) => {
    choreo.poses.forEach((step, i) => {
      if ((step.beats === undefined) === (step.durationMs === undefined))
        ctx.addIssue({
          code: "custom",
          path: ["poses", i],
          message: "Give each step a length in beats or in durationMs.",
        });
      if (step.vary !== undefined && step.beats !== undefined)
        ctx.addIssue({ code: "custom", path: ["poses", i, "vary"], message: "Only a millisecond step can vary." });
    });
    const units = new Set(choreo.poses.map((step) => (step.beats !== undefined ? "beats" : "ms")));
    if (units.size > 1)
      ctx.addIssue({
        code: "custom",
        path: ["poses"],
        message: "Use beats for every step or milliseconds for every step.",
      });
  });
export type Choreo = z.infer<typeof zChoreo>;

/** How a triggered choreo plays: once, in a loop, or a number of times. */
export const zChoreoPlay = z.union([z.enum(["once", "loop"]), z.number().int().min(1).max(64)]);
export type ChoreoPlay = z.infer<typeof zChoreoPlay>;

/** The beat clock: tempo and bar length (a song's, or a default). */
export interface BeatClock {
  bpm: number;
  beatsPerBar: number;
}

export const beatMs = (bpm: number) => 60000 / bpm;

/** A choreo whose steps are counted in beats. */
export const choreoInBeats = (choreo: Pick<Choreo, "poses">) => choreo.poses[0]?.beats !== undefined;

/** The sync a choreo uses: its own, else bar for beat steps and trigger for millisecond steps. */
export const choreoSync = (choreo: Pick<Choreo, "poses" | "sync">): ChoreoSync =>
  choreo.sync ?? (choreoInBeats(choreo) ? "bar" : "trigger");

/** One pass through the steps, in beats (beat steps) or ms (millisecond steps, at their nominal lengths). */
export const choreoLength = (choreo: Pick<Choreo, "poses">) =>
  choreo.poses.reduce((sum, step) => sum + (step.beats ?? step.durationMs ?? 0), 0);

/** How many passes a trigger plays (Infinity for a loop). */
export function choreoPasses(choreo: Pick<Choreo, "loop">, play?: ChoreoPlay): number {
  if (play === "loop") return Infinity;
  if (play === "once") return 1;
  if (typeof play === "number") return play;
  return choreo.loop ? Infinity : 1;
}

/** How long a one-off plays from its trigger, in ms (Infinity for a loop). Beat steps use the clock's tempo. */
export function choreoPlayMs(choreo: Choreo, clock: BeatClock, play?: ChoreoPlay): number {
  const passes = choreoPasses(choreo, play);
  if (!Number.isFinite(passes)) return Infinity;
  const pass = choreoInBeats(choreo) ? choreoLength(choreo) * beatMs(choreo.bpm ?? clock.bpm) : choreoLength(choreo);
  return pass * passes;
}

/** A small deterministic random number in [0, 1) from a string (pose pools and varied lengths agree on every screen). */
export function seeded(key: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return ((h >>> 0) % 100000) / 100000;
}

/** The lengths of the steps in one pass, in the choreo's own unit; varied millisecond steps differ pass to pass. */
function passLengths(choreo: Choreo, seed: string, pass: number): number[] {
  return choreo.poses.map((step, i) => {
    if (step.beats !== undefined) return step.beats;
    const length = step.durationMs ?? 0;
    if (!step.vary) return length;
    return Math.max(50, length * (1 + step.vary * (seeded(`${seed}:${pass}:${i}`) * 2 - 1)));
  });
}

/** Actual step length, including the deterministic variation of millisecond steps. */
export function choreoStepLength(choreo: Choreo, frame: Pick<ChoreoFrame, "step" | "pass">, seed: string): number {
  return passLengths(choreo, seed, frame.pass)[frame.step] ?? 0;
}

export interface ChoreoFrame {
  /** The step showing, its pass, and the pose it shows (a pool resolved), or undefined to keep the held pose. */
  step: number;
  pass: number;
  pose?: string;
  /** The step began at this position (so a caller can fire its gesture once, when it starts). */
  startedAt: number;
}

/**
 * The step showing at `elapsed` (beats for beat steps, ms for millisecond steps) since the choreo began, or
 * undefined before it begins and after a one-off ends. `seed` picks from pose pools and varies lengths the same way
 * on every screen.
 */
export function choreoFrame(choreo: Choreo, elapsed: number, passes: number, seed: string): ChoreoFrame | undefined {
  if (elapsed < 0) return undefined;
  // Fixed-length passes can be skipped arithmetically; varied ones are walked (they are short idles).
  const varied = choreo.poses.some((step) => step.vary);
  const nominal = choreoLength(choreo);
  if (nominal <= 0) return undefined;
  let pass = 0;
  let start = 0;
  if (!varied) {
    pass = Math.floor(elapsed / nominal);
    start = pass * nominal;
  }
  for (;;) {
    if (pass >= passes) return undefined;
    const lengths = passLengths(choreo, seed, pass);
    let at = start;
    for (let i = 0; i < lengths.length; i++) {
      if (elapsed < at + lengths[i]) {
        const pose = choreo.poses[i].pose;
        const picked = Array.isArray(pose) ? pose[Math.floor(seeded(`${seed}:${pass}:${i}:pose`) * pose.length)] : pose;
        return { step: i, pass, ...(picked ? { pose: picked } : {}), startedAt: at };
      }
      at += lengths[i];
    }
    start = at;
    pass += 1;
    if (pass > 100000) return undefined;
  }
}

/** Where a beat- or bar-synced choreo begins: the first beat (or bar line) at or after the trigger, in beats. */
export function syncedStart(triggerBeat: number, sync: ChoreoSync, beatsPerBar: number, toleranceBeats = 1e-6): number {
  if (sync === "beat") return Math.ceil(triggerBeat - toleranceBeats);
  if (sync === "bar") return Math.ceil((triggerBeat - toleranceBeats) / beatsPerBar) * beatsPerBar;
  return triggerBeat;
}

/** A groove's cycle length in ms; above 140 BPM the two-beat grooves run at half time so they never look frantic. */
export function grooveCycleMs(groove: Groove, bpm: number): number {
  const beats = GROOVE_BEATS[groove] * (bpm > 140 && GROOVE_BEATS[groove] > 1 ? 2 : 1);
  return beats * beatMs(bpm);
}

/** Where in its cycle a groove is at `ms` on the beat clock (0 = on the beat). */
export const groovePhaseMs = (ms: number, cycleMs: number) => ((ms % cycleMs) + cycleMs) % cycleMs;
